/** Nonsecret recovery marker only. Neither authentication nor messaging tokens are persisted here. */
const STORAGE_KEY = "dutyon.push.cleanup-pending";

export function createPushSession(timeoutMs = 5_000) {
  let driver: (() => Promise<void>) | undefined;
  let generation = 0;
  let pending = false;
  let running: Promise<boolean> | undefined;
  let snapshot = { pending, recovering: false };
  const listeners = new Set<() => void>();
  const work = new Set<Promise<unknown>>();
  const publish = () => {
    snapshot = { pending, recovering: !!running };
    for (const listener of listeners) listener();
  };
  const persist = () => {
    try {
      if (pending) localStorage.setItem(STORAGE_KEY, "1");
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Every native process starts behind the cleanup barrier, including storage failure/restart.
    }
  };
  const invalidate = () => {
    if (!driver) return;
    generation++;
    pending = true;
    persist();
    publish();
  };
  const recover = async (): Promise<boolean> => {
    if (!driver) return true;
    if (!pending) return true;
    if (!running) {
      const owner = generation;
      const invalidateToken = driver;
      const operation = Promise.allSettled([...work]).then(invalidateToken);
      // Keep the raw operation tracked after timeout. A late delete must never race a new token.
      running = operation
        .then(
          () => {
            if (owner === generation) {
              pending = false;
              persist();
            }
            return !pending;
          },
          () => false,
        )
        .finally(() => {
          running = undefined;
          publish();
          // A login/logout while deletion was running needs one fresh, serialized cleanup.
          // Retry failures only on another explicit trigger; never spin on an offline SDK.
          if (owner !== generation) void recover();
        });
      publish();
    }
    const operation = running;
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([
        operation,
        new Promise<boolean>((resolve) => {
          timer = setTimeout(() => resolve(false), timeoutMs);
        }),
      ]);
    } finally {
      if (timer) clearTimeout(timer);
    }
  };
  return {
    configure(invalidateToken: () => Promise<void>) {
      if (driver) return;
      driver = invalidateToken;
      // Also rotate on cold start: a failed marker write must not silently resume an old token.
      invalidate();
    },
    invalidate,
    recover,
    capture: () => generation,
    accepts: (lease: number) => !pending && lease === generation,
    async run<T>(lease: number, task: () => Promise<T>): Promise<T | null> {
      if (pending || lease !== generation) return null;
      const operation = Promise.resolve().then(() => {
        if (pending || lease !== generation) return null;
        return task();
      });
      work.add(operation);
      try {
        const result = await operation;
        return !pending && lease === generation ? result : null;
      } finally {
        work.delete(operation);
      }
    },
    getSnapshot: () => snapshot,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export const pushSession = createPushSession();
